import { useElementMotionStore } from "@/lib/stores/element-motion-store/element-motion-store";
import { useCurrentScale } from "remotion";
import { ResizeHandle, ResizeHandleType } from "./ResizeHandle";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { BoundingBox } from "@/lib/stores/element-motion-store/element-motion-state";
import SelectionActions from "./SelectionActions";

type Props = {
  selectionBoundingBox: BoundingBox | null;
  isMoving: boolean;
  isResizing: boolean;
  resizeHandleType: ResizeHandleType | null;
};

export default function SelectionOutline({
  selectionBoundingBox,
  isMoving,
  isResizing,
  resizeHandleType,
}: Props) {
  const scale = useCurrentScale() || 1;
  const outlineWidth = Math.round(2 / scale);

  const isMultipleSelected = useElementMotionStore((state) => {
    return state.selectedElementIds.size > 1;
  });

  const getElementsMapWithSceneAndIndex = useElementMotionStore(
    (state) => state.service.getElementsMapWithSceneAndIndex,
  );
  const selectedElementIds = useElementMotionStore(
    useShallow((state) => state.selectedElementIds),
  );

  const selectionIncludesText = useMemo(() => {
    const elementsMap = getElementsMapWithSceneAndIndex();

    for (const id of selectedElementIds) {
      const element = elementsMap[id];
      if (element?.element.type === "TEXT") {
        return true;
      }
    }

    return false;
  }, [selectedElementIds, getElementsMapWithSceneAndIndex]);

  if (!selectionBoundingBox) {
    return null;
  }

  const { left, top, right, bottom } = selectionBoundingBox;

  const selectionWidth = (right - left) * scale;
  const selectionHeight = (bottom - top) * scale;

  const isEdgeHandleVisible = selectionWidth > 50 && selectionHeight > 50;

  const outlineStyle = isMultipleSelected
    ? `${outlineWidth}px dashed var(--color-tertiary-400)`
    : `${outlineWidth}px solid var(--color-tertiary-400)`;

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        transform: `translate3d(${left + outlineWidth}px, ${top + outlineWidth}px, 0)`,
        width: Math.max(right - left, 0) - outlineWidth * 2,
        height: Math.max(bottom - top, 0) - outlineWidth * 2,
        outline: outlineStyle,
        willChange: "transform, width, height",
      }}
    >
      {!selectionIncludesText && (
        <>
          <ResizeHandle
            type="top"
            currentResizeHandleType={resizeHandleType}
            isEdgeHandleVisible={isEdgeHandleVisible}
            isMoving={isMoving}
          />
          <ResizeHandle
            type="left"
            currentResizeHandleType={resizeHandleType}
            isEdgeHandleVisible={isEdgeHandleVisible}
            isMoving={isMoving}
          />
          <ResizeHandle
            type="right"
            currentResizeHandleType={resizeHandleType}
            isEdgeHandleVisible={isEdgeHandleVisible}
            isMoving={isMoving}
          />
          <ResizeHandle
            type="bottom"
            currentResizeHandleType={resizeHandleType}
            isEdgeHandleVisible={isEdgeHandleVisible}
            isMoving={isMoving}
          />
        </>
      )}

      <ResizeHandle
        type="top-left"
        currentResizeHandleType={resizeHandleType}
        isMoving={isMoving}
      />
      <ResizeHandle
        type="top-right"
        currentResizeHandleType={resizeHandleType}
        isMoving={isMoving}
      />
      <ResizeHandle
        type="bottom-left"
        currentResizeHandleType={resizeHandleType}
        isMoving={isMoving}
      />
      <ResizeHandle
        type="bottom-right"
        currentResizeHandleType={resizeHandleType}
        isMoving={isMoving}
      />

      <SelectionActions isMoving={isMoving} isResizing={isResizing} />
    </div>
  );
}
